import { AppointmentDetailDto, AppointmentListData, AppointmentStatus, MerchantUserDto, TimeOnly } from "@/models/club";
import { BarberAppointment } from "@/services/barberDashboard/types";
import { localDate } from "@/services/barberDashboard/utils";
import { clockText, durationText, faNumber, isoDate, weekdayName } from "./format";
import { UpcomingItem } from "./UpcomingAppointments";

// Cancelled, failed-payment and refunded bookings are not visits.
const notVisits = new Set([AppointmentStatus.Value2, AppointmentStatus.Value3, AppointmentStatus.Value4]);

const pad = (value?: number) => String(value ?? 0).padStart(2, "0");

// Times arrive as "HH:mm:ss" even though the generated type describes an object.
export function timeString(value?: TimeOnly | string | null) {
  if (typeof value === "string") return value;
  if (value && typeof value === "object") return `${pad(value.hour)}:${pad(value.minute)}:${pad(value.second)}`;
  return "";
}

export type CalendarVisit = { appointment: AppointmentDetailDto; user: MerchantUserDto | undefined; start: Date; end: Date };

// Every visit on the calendar response with its real start and end; `count` also includes visits without usable times.
export function calendarVisits(data: AppointmentListData | undefined, now = new Date()) {
  const rows = (data ?? []).flatMap((entry) =>
    (entry.appointmentCalendar ?? []).flatMap((day) =>
      (day.appointments ?? [])
        .filter((appointment) => appointment.status === undefined || !notVisits.has(appointment.status))
        .map((appointment) => ({ appointment, user: entry.user })),
    ),
  );
  const visits = rows
    .map(({ appointment, user }) => {
      const day = appointment.appointmentDate ? localDate(appointment.appointmentDate) : now;
      const start = atTime(day, timeString(appointment.startTime));
      const parsedEnd = start && atTime(day, timeString(appointment.endTime));
      // A visit ending after midnight comes back with an earlier end time.
      const end = start && parsedEnd && parsedEnd <= start ? new Date(parsedEnd.getTime() + 86_400_000) : parsedEnd || start;
      return { appointment, user, start, end };
    })
    .filter((visit): visit is CalendarVisit => Boolean(visit.start && visit.end));
  return { visits, count: rows.length };
}

// Plain Date math: moment-jalaali reads a time-only string as year 0000.
function atTime(day: Date, time: string) {
  const [hour, minute, second] = time.split(":").map(Number);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;
  const date = new Date(day);
  date.setHours(hour, minute, Number.isFinite(second) ? second : 0, 0);
  return date;
}

const minutesBetween = (from: Date, to: Date) => Math.round((to.getTime() - from.getTime()) / 60_000);

function chipFor(start: Date, end: Date, now: Date): UpcomingItem["chip"] {
  if (start <= now && end > now) return { text: "در حال انجام", tone: "live" };
  const minutes = Math.ceil((start.getTime() - now.getTime()) / 60_000);
  if (minutes > 0 && minutes <= 60) return { text: `${faNumber(minutes)} دقیقه دیگر`, tone: "soon" };
  return undefined;
}

// Today's calendar for the whole salon: every visit that has not finished yet, soonest first.
export function upcomingFromCalendar(data: AppointmentListData | undefined, now = new Date()) {
  const { visits, count } = calendarVisits(data, now);
  const upcoming = visits
    .filter(({ start, end }) => end > now || start > now)
    .sort((a, b) => a.start.getTime() - b.start.getTime())
    .map<UpcomingItem>(({ appointment, user, start, end }) => {
      const customer = `${appointment.customerFirstName ?? ""} ${appointment.customerLastName ?? ""}`.trim();
      const staff = appointment.merchantUserFirstName?.trim() || user?.firstName?.trim() || "";
      return {
        key: String(appointment.id ?? `${user?.id}-${start.getTime()}`),
        time: clockText(timeString(appointment.startTime)),
        duration: durationText(minutesBetween(start, end)),
        customer: customer || "مشتری بدون نام",
        detail: [appointment.merchantServiceTitle || "خدمت ثبت‌شده", staff].filter(Boolean).join(" · "),
        staffColor: user?.calendarColor ?? null,
        phone: appointment.customerMobileNumber,
        chip: chipFor(start, end, now),
      };
    });
  return { upcoming, visitsToday: count };
}

// A stylist's own upcoming visits from their report; later days are labelled with the day.
export function upcomingFromReport(list: BarberAppointment[] | undefined, now = new Date()) {
  const today = isoDate(now);
  const tomorrowDate = new Date(now);
  tomorrowDate.setDate(now.getDate() + 1);
  const tomorrow = isoDate(tomorrowDate);
  return (list ?? []).map<UpcomingItem>((appointment) => {
    const day = localDate(appointment.appointmentDate);
    const dayKey = isoDate(day);
    const start = atTime(day, appointment.startTime);
    const end = start && new Date(start.getTime() + Math.max(appointment.durationMinutes, 0) * 60_000);
    const chip: UpcomingItem["chip"] =
      dayKey === today
        ? start && end
          ? chipFor(start, end, now)
          : undefined
        : { text: dayKey === tomorrow ? "فردا" : weekdayName(day), tone: "day" };
    return {
      key: String(appointment.appointmentId),
      time: clockText(appointment.startTime),
      duration: durationText(appointment.durationMinutes),
      customer: appointment.customerFullName?.trim() || "مشتری بدون نام",
      detail: appointment.serviceTitle || "خدمت ثبت‌شده",
      phone: appointment.customerPhoneNumber,
      chip,
    };
  });
}
